<script lang="ts">
    import {UserFeedbackService} from 'shared/services';
    import {getInjector, getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {ScannableTextValueEditorLocalizations, ScannableTextValueEditorOptions} from './types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormSubmitRequestEvent} from '../../../events';
    import type {TextValueEditorValidations} from '../text/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {formControls} from '../../forms/use.form-controls';
    import {style} from 'svelte-forms';
    import {createEventDispatcher, onDestroy, onMount, tick} from 'svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {barcodeScanner} from './use.barcode-scanner';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {validatorFactory} from '../text/utils';
    import {emptyAsNullFormatter, emptyAsNullParser, trim} from '../_shared/editors-utils';
    import {getContext} from 'svelte';
    import {BarcodeScannerService} from 'shared/barcode-scanner/barcode-scanner.service';
    import {createInputDescription} from '../../description/utils';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {firstValueFrom} from 'rxjs';
    import {required} from '../../forms/use.required';
    import {INPUT_GROUP_HIDDEN_CLASS} from './utils';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import BarcodeScannerInputControl from './barcode-scanner-input-control/BarcodeScannerInputControl.svelte';
    import Description from '../../description/Description.svelte';

    export let model: string;
    export let formControlOptions: FormControlOptions;
    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;
    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;
    export let options: ScannableTextValueEditorOptions;
    export let validations: TextValueEditorValidations;

    const localize: EditorsLocalizationFunction<ScannableTextValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const dispatch = createEventDispatcher<{'form-submit-request': FormSubmitRequestEvent['detail']}>();
    const barcodeScannerService = getInjector().getByClass(BarcodeScannerService);
    const userFeedbackService = getInjector().getByClass(UserFeedbackService);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations, 'text'))
        .withOptions(formControlOptions)
        .withModifiers([trim(options), emptyAsNullParser(options)])
        .withFormatters([emptyAsNullFormatter(options)])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    let isScanningSubsystemInitialized = false;
    let inputElement: HTMLInputElement;
    let inputGroupElement;

    onMount(() => tick().then(() => isScanningSubsystemInitialized = true)); // to ensure that input will focus after the system is ready

    onDestroy(() => subscription.unsubscribe());

    async function onScan() {
        if (options.autosubmit) {
            await firstValueFrom(formControl.getModelInputChangeEvent$()); // wait until scanned value gets in model
            dispatch('form-submit-request', {submitter: inputElement});
        }
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

<div class={INPUT_GROUP_HIDDEN_CLASS} bind:this={inputGroupElement}>
    <input
        bind:this={inputElement}
        class="form-control input-{size}"
        type="text"
        id={editorId}
        name={editorName}
        placeholder={placeholder}
        disabled={isDisabled}
        maxlength={validations.maxlength}
        minlength={validations.minlength}
        use:required={validations.required}
        use:formControls={formControl}
        use:barcodeScanner={{formControl, barcodeScannerService, userFeedbackService}}
        use:focusOnMount={isFocused && isScanningSubsystemInitialized}
        use:touched={{formControl}}
        use:ariaErrormessage={formControl}
        use:ariaInvalid={formControl}
        use:ariaDescribedby={inputDescription}
        use:style={{field: formControl.getFieldStateAsStore()}}
        on:barcode-scan={onScan}
        data-main-input/>

    <BarcodeScannerInputControl allowedAutomaticStart={options.allowedAutomaticStart} {isDisabled} {size} {inputElement} {inputGroupElement}>
        <svelte:fragment slot="stop-scanning">
            {localize('stopScanning')}
        </svelte:fragment>
        <svelte:fragment slot="start-scanning">
            {localize('scanWithCamera')}
        </svelte:fragment>
    </BarcodeScannerInputControl>
</div>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>

<style lang="less">
    // input element in read only mode for camera scanner values
    // shows clickable cursor for better understanding
    input {
        &:read-only {
            cursor: pointer;
        }

        &:-moz-read-only {
            cursor: pointer; // same as ^, but for mozzila browsers
        }
    }
</style>